import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ExternalLink, Globe, Calendar, Package, DollarSign } from 'lucide-react';

const UserViewOrderModal = ({ order, isOpen, onClose }) => {
  if (!order) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Order Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Order Summary */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg border border-cyan-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Order ID</p>
                <p className="font-mono font-semibold text-lg">{order.id?.substring(0, 8)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Order Date</p>
                <p className="font-semibold text-lg">
                  {new Date(order.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <Badge className={getStatusColor(order.status)}>
                  {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Payment Status</p>
                <Badge className={getPaymentStatusColor(order.payment_status)}>
                  {order.payment_status?.charAt(0).toUpperCase() + order.payment_status?.slice(1)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Plan Details */}
          <div className="border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="h-5 w-5 text-cyan-600" />
              <h3 className="text-lg font-semibold">Plan Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Plan Name</p>
                <p className="font-semibold text-lg">{order.plan_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Amount Paid</p>
                <p className="font-semibold text-lg flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  {order.amount?.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Backlinks</p>
                <p className="font-semibold">{order.backlinks} Publications</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Domain Authority</p>
                <p className="font-semibold">DA {order.da_range}</p>
              </div>
            </div>
          </div>

          {/* Order Requirements */}
          {order.order_details && (
            <div className="border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Your Requirements</h3>
              <div className="space-y-4">
                {order.order_details.website_url && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Website URL</p>
                    <a 
                      href={order.order_details.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2"
                    >
                      {order.order_details.website_url}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                )}

                {order.order_details.keywords && order.order_details.keywords.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Target Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {order.order_details.keywords.map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-cyan-100 text-cyan-800">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {order.order_details.image_url && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Image URL</p>
                    <a 
                      href={order.order_details.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700 text-sm break-all"
                    >
                      {order.order_details.image_url}
                    </a>
                  </div>
                )}

                {order.order_details.youtube_url && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">YouTube URL</p>
                    <a 
                      href={order.order_details.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700 text-sm break-all"
                    >
                      {order.order_details.youtube_url}
                    </a>
                  </div>
                )}

                {order.order_details.article_content && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Article Content</p>
                    <div className="bg-gray-50 p-4 rounded border max-h-60 overflow-y-auto">
                      <p className="text-sm whitespace-pre-wrap">{order.order_details.article_content}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Backlinks Section */}
          {order.backlinks_data && order.backlinks_data.length > 0 ? (
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5 text-cyan-600" />
                <h3 className="text-lg font-semibold">Your Publications ({order.backlinks_data.length})</h3>
              </div>
              <div className="space-y-3">
                {order.backlinks_data.map((backlink, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={backlink.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {backlink.status}
                        </Badge>
                        {backlink.published_date && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(backlink.published_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-cyan-600 break-all">
                        {backlink.url}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(backlink.url, '_blank')}
                      className="ml-4"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="border rounded-lg p-6 bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Backlinks In Progress</h3>
                  <p className="text-sm text-blue-700">
                    {order.status === 'completed' 
                      ? 'Your publications will be visible here once they are added by our team.'
                      : 'Our team is working on your publications. They will appear here once completed.'
                    }
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Expected delivery: 7-14 business days
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserViewOrderModal;
